import React, { createContext, useContext, useEffect, useState } from "react";
import { fetchStudioCapabilities } from "../Helper/studioCapabilities";
import {
  availableWorkspaces,
  getWorkspace,
  readStoredWorkspace,
  writeStoredWorkspace,
  workspaceIdFromPortal,
} from "./workspaceRegistry";

const ActiveWorkspaceContext = createContext(null);

export function ActiveWorkspaceProvider({ children }) {
  const [caps, setCaps] = useState(null);
  const [workspaceId, setWorkspaceId] = useState(null);
  const [entityId, setEntityId] = useState(null);
  const [entityLabel, setEntityLabel] = useState(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let cancelled = false;
    fetchStudioCapabilities().then((c) => {
      if (cancelled) return;
      setCaps(c);
      const allowed = availableWorkspaces(c);
      const stored = readStoredWorkspace();
      let nextId = null;
      let nextEntity = null;
      let nextLabel = null;

      if (
        stored &&
        stored.workspaceId !== "personal" &&
        allowed.some((w) => w.id === stored.workspaceId)
      ) {
        nextId = stored.workspaceId;
        nextEntity = stored.entityId;
        nextLabel = stored.entityLabel;
      } else if (allowed.length) {
        const preferred = allowed[0];
        nextId = preferred.id;
        if (preferred.entityType === "creator" && c.creator_ids && c.creator_ids[0]) {
          nextEntity = String(c.creator_ids[0]);
        } else if (
          preferred.entityType === "organization" &&
          c.organization_ids &&
          c.organization_ids[0]
        ) {
          nextEntity = String(c.organization_ids[0]);
        } else if (
          preferred.entityType === "project" &&
          c.project_ids &&
          c.project_ids[0]
        ) {
          nextEntity = String(c.project_ids[0]);
        }
      }

      setWorkspaceId(nextId);
      setEntityId(nextEntity);
      setEntityLabel(nextLabel);
      if (nextId) {
        writeStoredWorkspace(nextId, nextEntity, nextLabel);
      }
      setReady(true);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const activate = (nextWorkspaceId, nextEntityId, nextEntityLabel) => {
    setWorkspaceId(nextWorkspaceId);
    setEntityId(nextEntityId || null);
    setEntityLabel(nextEntityLabel || null);
    writeStoredWorkspace(nextWorkspaceId, nextEntityId, nextEntityLabel);
  };

  const activateFromPortal = (portal, entity) => {
    const id = workspaceIdFromPortal(portal);
    if (!id) return null;
    const entId = entity && entity.id != null ? String(entity.id) : null;
    activate(id, entId, entity && entity.label ? entity.label : null);
    const ws = getWorkspace(id);
    if (entity && entity.path) return entity.path;
    if (entId && ws) return ws.route + "/" + entId;
    return ws ? ws.route : "/account";
  };

  const value = {
    ready,
    caps,
    workspaceId,
    entityId,
    entityLabel,
    workspace: getWorkspace(workspaceId),
    available: availableWorkspaces(caps),
    activate,
    activateFromPortal,
    setEntityLabel,
  };

  return (
    <ActiveWorkspaceContext.Provider value={value}>
      {children}
    </ActiveWorkspaceContext.Provider>
  );
}

export function useActiveWorkspace() {
  return useContext(ActiveWorkspaceContext);
}

export default ActiveWorkspaceContext;
